import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { Banknote, CreditCard, ArrowRightLeft, CalendarDays, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react'
import {
  type Membership, type CreateMembershipInput, type UpdateMembershipInput,
  createMembership, updateMembership,
} from '@/api/memberships'
import { listPlans, type MembershipPlan } from '@/api/plans'
import { MemberSearch } from '@/components/shared/MemberSearch'
import type { User } from '@/api/users'

const PAYMENT_METHODS = [
  { id: 'cash',     label: 'Efectivo',       icon: Banknote },
  { id: 'card',     label: 'Tarjeta',        icon: CreditCard },
  { id: 'transfer', label: 'Transferencia',  icon: ArrowRightLeft },
]

function fmtPrice(n: number) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n)
}
function fmtDate(iso: string) {
  if (!iso) return '—'
  return new Date(iso + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
}
function addDays(dateStr: string, days: number) {
  const d = new Date(dateStr + 'T12:00:00Z')
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().split('T')[0]
}
// Local calendar date, not toISOString() — that shifts by the browser's UTC
// offset and can silently roll "today" to the next or previous day.
function todayLocal(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

interface Props {
  open: boolean
  mode: 'create' | 'renew' | 'edit' | 'correct-start'
  userId?: string
  prefillUser?: User
  prefillPlanId?: string
  membership?: Membership | null
  onClose: () => void
  onSaved: (membership: Membership) => void
}

export function MembershipForm({ open, mode, userId, prefillUser, prefillPlanId, membership, onClose, onSaved }: Props) {
  const [plans, setPlans]               = useState<MembershipPlan[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(prefillUser ?? null)
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [startDate, setStartDate]       = useState('')
  const [endDate, setEndDate]           = useState('')
  const [status, setStatus]             = useState<'active' | 'expired' | 'suspended' | 'cancelled'>('active')
  const [autoRenew, setAutoRenew]       = useState(false)
  const [notes, setNotes]               = useState('')
  const [paymentMethodId, setPaymentMethodId] = useState('')
  const [showExtra, setShowExtra]       = useState(false)
  const [saving, setSaving]             = useState(false)

  useEffect(() => {
    listPlans().then(r => setPlans(r.plans.filter(p => p.is_active))).catch(() => {})
  }, [])

  useEffect(() => {
    if (!open) return
    const today = todayLocal()
    if (mode === 'create') {
      setSelectedUser(prefillUser ?? null); setSelectedPlanId(prefillPlanId ?? ''); setStartDate(today)
      setEndDate(''); setAutoRenew(false); setNotes(''); setStatus('active'); setPaymentMethodId('')
    } else if (mode === 'renew' && membership) {
      setSelectedPlanId(membership.plan_id); setStartDate(String(membership.end_date).split('T')[0])
      setEndDate(''); setAutoRenew(false); setNotes(''); setStatus('active'); setPaymentMethodId('')
    } else if ((mode === 'edit' || mode === 'correct-start') && membership) {
      setSelectedPlanId(membership.plan_id); setStartDate(String(membership.start_date).split('T')[0])
      setEndDate(String(membership.end_date).split('T')[0])
      setStatus(membership.status as typeof status); setAutoRenew(membership.auto_renew); setNotes(membership.notes ?? '')
    }
  }, [open, mode, membership, prefillUser])

  // Auto-calculate end date when plan or start date changes
  const selectedPlan = plans.find(p => p.id === selectedPlanId)
  useEffect(() => {
    if (selectedPlan && startDate && mode !== 'edit') {
      setEndDate(addDays(startDate, selectedPlan.duration_days))
    }
  }, [startDate, selectedPlanId, mode, selectedPlan])

  const resolvedUserId = selectedUser?.id ?? membership?.user_id ?? userId ?? ''
  const needsPayment   = mode === 'create' || mode === 'renew'
  const isCorrect      = mode === 'correct-start'
  const canSubmit      = selectedPlanId && resolvedUserId && (!needsPayment || paymentMethodId) && startDate

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) { toast.error('Completa los campos requeridos'); return }
    setSaving(true)
    try {
      let saved: Membership
      if (mode === 'create' || mode === 'renew') {
        const r = await createMembership({
          userId: resolvedUserId, planId: selectedPlanId, startDate,
          endDate: endDate || undefined, autoRenew,
          notes: notes || undefined, paymentMethodId: paymentMethodId || undefined,
          renew: mode === 'renew',
        } as CreateMembershipInput)
        saved = r.membership
        toast.success(mode === 'create' ? 'Membresía asignada' : 'Membresía renovada')
      } else {
        if (!membership) throw new Error('Membresía no encontrada')
        const r = await updateMembership(membership.id, {
          planId: selectedPlanId, startDate, endDate, status, autoRenew,
          notes: notes || undefined,
        } as UpdateMembershipInput)
        saved = r.membership
        toast.success('Membresía actualizada')
      }
      onSaved(saved); onClose()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al guardar membresía')
    } finally {
      setSaving(false)
    }
  }

  const title = { create: 'Nueva membresía', renew: 'Renovar membresía', 'correct-start': 'Corregir inicio', edit: 'Editar membresía' }[mode]

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-surface border-border text-text-primary sm:max-w-3xl flex flex-col max-h-[92vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-base font-bold">{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5 min-h-0">

            {/* ── Miembro (solo sin userId fijo) ── */}
            {!userId && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Miembro</p>
                {(mode === 'edit' || mode === 'renew' || mode === 'correct-start') ? (
                  <p className="text-text-primary text-sm font-medium">{selectedUser?.name ?? membership?.user_id}</p>
                ) : (
                  <MemberSearch onSelect={setSelectedUser} value={selectedUser} />
                )}
              </div>
            )}

            {/* ── Grid principal: Plan (izq) + Pago y Fechas (der) ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0">

              {/* ── Columna izquierda: Plan ── */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">Plan</p>
                {isCorrect ? (
                  <div className="flex items-center gap-3 bg-background rounded-xl px-4 py-3 border border-border">
                    <div className="flex-1">
                      <p className="text-text-primary text-sm font-semibold">{selectedPlan?.name}</p>
                      <p className="text-text-muted text-xs">{selectedPlan?.duration_days} días</p>
                    </div>
                    <p className="text-accent font-bold text-base">{selectedPlan ? fmtPrice(selectedPlan.price) : ''}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2.5">
                    {plans.map(plan => (
                      <button
                        key={plan.id} type="button"
                        onClick={() => setSelectedPlanId(plan.id)}
                        className={`relative flex flex-col rounded-xl border-2 px-4 py-3 text-left transition-all ${
                          selectedPlanId === plan.id
                            ? 'border-accent bg-accent/8'
                            : 'border-border bg-background hover:border-accent/40 hover:bg-background'
                        }`}
                      >
                        {selectedPlanId === plan.id && (
                          <CheckCircle2 className="absolute top-2.5 right-2.5 w-4 h-4 text-accent" />
                        )}
                        <span className="text-sm font-bold pr-5 text-text-primary">
                          {plan.name}
                        </span>
                        {plan.description && (
                          <span className="text-text-muted text-[11px] mt-0.5 line-clamp-1">{plan.description}</span>
                        )}
                        <div className="flex items-end justify-between mt-2">
                          <span className={`text-xs ${selectedPlanId === plan.id ? 'text-accent/70' : 'text-text-muted'}`}>
                            {plan.duration_days} días
                          </span>
                          <span className={`text-base font-extrabold leading-none ${selectedPlanId === plan.id ? 'text-accent' : 'text-text-primary'}`}>
                            {fmtPrice(plan.price)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Columna derecha: Pago + Fechas + Estado ── */}
              <div className="flex flex-col gap-5">

                {/* Método de pago */}
                {needsPayment && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">Método de pago</p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {PAYMENT_METHODS.map(m => {
                        const Icon = m.icon
                        const sel = paymentMethodId === m.id
                        return (
                          <button
                            key={m.id} type="button"
                            onClick={() => setPaymentMethodId(m.id)}
                            className={`flex flex-col items-center gap-2 py-3.5 rounded-xl border-2 transition-all ${
                              sel
                                ? 'border-emerald-500 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400'
                                : 'border-border bg-background text-text-muted hover:border-accent/40 hover:text-text-primary'
                            }`}
                          >
                            <Icon className={`w-5 h-5 ${sel ? 'text-emerald-700 dark:text-emerald-400' : 'text-text-muted'}`} />
                            <span className="text-xs font-semibold">{m.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Fechas */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Período</p>
                    {selectedPlan && startDate && endDate && (
                      <span className="text-xs text-accent font-semibold">{selectedPlan.duration_days} días</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-text-muted text-[10px] block mb-1">Inicio</label>
                      <input
                        type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required
                        className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                    </div>
                    <CalendarDays className="w-4 h-4 text-text-muted mt-4 shrink-0" />
                    <div className="flex-1">
                      <label className="text-text-muted text-[10px] block mb-1">Vence</label>
                      {isCorrect ? (
                        <p className="text-text-primary text-sm font-medium py-2.5 px-3">{endDate ? fmtDate(endDate) : '—'}</p>
                      ) : (
                        <input
                          type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required
                          className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                        />
                      )}
                    </div>
                  </div>
                  {isCorrect && (
                    <p className="text-xs text-text-muted mt-2">La fecha de fin se recalcula automáticamente desde el plan.</p>
                  )}
                </div>

                {/* Estado (solo edit) */}
                {mode === 'edit' && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Estado</p>
                    <select
                      value={status} onChange={e => setStatus(e.target.value as typeof status)}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                    >
                      <option value="active">Activa</option>
                      <option value="expired">Expirada</option>
                      <option value="suspended">Suspendida</option>
                      <option value="cancelled">Cancelada</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* ── Extras colapsables (ancho completo) ── */}
            <div>
              <button type="button" onClick={() => setShowExtra(p => !p)}
                className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors">
                {showExtra ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Notas y opciones adicionales
              </button>
              {showExtra && (
                <div className="flex flex-col gap-3 mt-3">
                  <textarea
                    value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Observaciones opcionales..." rows={2} maxLength={500}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent resize-none"
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={autoRenew} onChange={e => setAutoRenew(e.target.checked)} className="accent-accent w-4 h-4" />
                    <span className="text-sm text-text-primary">Renovación automática</span>
                  </label>
                </div>
              )}
            </div>

          </div>

          {/* ── Resumen + acciones ── */}
          <div className="shrink-0 border-t border-border">
            {selectedPlan && startDate && endDate && (needsPayment ? paymentMethodId : true) && (
              <div className="px-6 py-3 bg-background/60 flex items-center justify-between gap-4">
                <div>
                  <p className="text-text-primary text-sm font-semibold">{selectedPlan.name}</p>
                  <p className="text-text-muted text-xs">{fmtDate(startDate)} → {fmtDate(endDate)}</p>
                </div>
                <p className="text-accent text-lg font-extrabold shrink-0">{fmtPrice(selectedPlan.price)}</p>
              </div>
            )}
            <div className="px-6 py-4 flex gap-3 justify-end">
              <button type="button" onClick={onClose}
                className="px-5 py-2 text-sm text-text-muted hover:text-text-primary transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={saving || !canSubmit}
                className="px-6 py-2 bg-accent text-background text-sm font-bold rounded-lg hover:bg-accent-hover disabled:opacity-40 transition-colors">
                {saving ? 'Guardando...' : mode === 'create' ? 'Asignar membresía' : mode === 'renew' ? 'Renovar' : 'Guardar'}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
